import { api } from '@api/index';
import {
  CertificationType,
  SettingType,
  VoucherEffectType,
  VoucherRecommendationType,
} from '@constants';
import {
  IBalloonHistory,
  IBlockedUser,
  ICertification,
  ICustomToken,
  IEFinderObject,
  IFanClub,
  IFanUser,
  IFirebaseEmailCredential,
  IGuestToken,
  IInvitationCode,
  IMessage,
  IOneTimeToken,
  IPagination,
  IRecoveryUser,
  ISignup,
  ISimpleUser,
  IStory,
  IUserMarketInventory,
  IUserMe,
  IUserRestriction,
  IVoucher,
  IVoucherRecommendation,
} from '@model';
import { toQueryString } from '@utils/misc';

export const fetchMe = async (accessToken?: string) => {
  const { data } = await api.get<IUserMe>(
    '/users/me/',
    accessToken ? { headers: { Authorization: accessToken } } : undefined
  );
  return data;
};

export type PatchMePayload = {
  nickname: string;
  introduce: string;
  picture?: IEFinderObject<'image'>;
};

export const patchMe = async (payload: PatchMePayload) => {
  const { data } = await api.patch<IUserMe>('/users/me/', payload);
  return data;
};

export const fetchUser = async (userId: number | undefined) => {
  if (userId) {
    const { data } = await api.get(`/users/${userId}/`);
    return data;
  }
};

export type FetchUserListPayload = {
  ids: number[];
};
export const fetchUserList = async (ids: number[]) => {
  if (ids?.length) {
    const { data } = await api.get(`/users/${toQueryString({ ids })}`);
    return data?.items;
  }
};

export const fetchUserBalloonHistories = async (page = 1, perPage = 20) => {
  const { data } = await api.get<IPagination<IBalloonHistory>>(
    `/users/me/balloon-histories/${toQueryString({ page, perPage })}`
  );
  return data;
};

export const fetchBlockedUsers = async (page = 1, perPage = 20) => {
  const params = toQueryString({ page, perPage });
  const { data } = await api.get<IPagination<IBlockedUser>>(
    `/users/me/block-users/${params}`
  );
  return data;
};

export const postToggleBlockUser = async (userId: number) => {
  const { data } = await api.post(`/users/${userId}/block/`);
  return data;
};

export const fetchUserRestriction = async (id: number) => {
  const { data } = await api.get<IUserRestriction>(`/users/${id}/restriction/`);
  return data;
};

export const fetchMyRestrictionList = async () => {
  const { data } = await api.get<IUserRestriction>('/users/me/restrictions/');
  return data;
};

/**
 * market items
 */

export const fetchMyMarketItemInventories = async () => {
  const { data } = await api.get<IUserMarketInventory[]>(
    '/users/me/market-item-inventories/'
  );
  return data;
};

export const consumeMyMarketItemInventory = async (
  marketItemInventoryId: number
) => {
  const { data } = await api.post<IUserMarketInventory>(
    `/users/me/market-item-inventories/${marketItemInventoryId}/consumptions/`
  );
  return data;
};

export const patchMyMarketItemInventory = async ({
  id,
  status,
}: Pick<IUserMarketInventory, 'id' | 'status'>) => {
  const { data } = await api.patch<IUserMarketInventory>(
    `/users/me/market-item-inventories/${id}/`,
    { status }
  );
  return data;
};

export const UserSettingKeys = {
  followingUpdatePushStatus: 'following_update_push_status',
  newEpisodePushStatus: 'new_episode_push_status',
  newReplyPushStatus: 'new_reply_push_status',
  newGroundPushStatus: 'new_ground_push_status',
};
export type UserSettingValue =
  | 'enable'
  | 'partial_enable'
  | 'disable'
  | 'default';

export interface ISetting {
  id: string;
  setting: {
    [key in keyof typeof UserSettingKeys]: UserSettingValue;
  };
}

export const fetchUserSettings = async (
  objectType: SettingType,
  objectId: number
) => {
  const { data } = await api.get<ISetting>(
    `/users/me/settings/${objectType}-${objectId}/`
  );
  return data;
};

export const postUserSettings = async (
  objectType: SettingType,
  objectId: number,
  key: keyof typeof UserSettingKeys,
  value: UserSettingValue
) => {
  const id = `${objectType}-${objectId}`;
  const { data } = await api.post(`/users/me/settings/`, {
    objectId: id,
    key: UserSettingKeys[key],
    value,
  });
  return { id, ...data } as ISetting;
};

export const postToggleFollowUser = async (id: number) => {
  await api.post(`/users/${id}/follow/`);
};

export const fetchFollowUsers = async (
  userId: number,
  page = 1,
  perPage = 20
) => {
  const { data } = await api.get<IPagination<ISimpleUser>>(
    `/users/${userId}/followers/${toQueryString({ page, perPage })}`
  );
  return data;
};

export const fetchFollowingUsers = async (
  userId: number,
  page = 1,
  perPage = 20
) => {
  const { data } = await api.get<IPagination<ISimpleUser>>(
    `/users/${userId}/followings/${toQueryString({ page, perPage })}`
  );
  return data;
};

export const fetchCertification = async (type: CertificationType) => {
  const { data } = await api.get<ICertification>(
    `/users/me/certification/${toQueryString({ type })}`
  );
  return data;
};

/**
 * Fan
 */

export const fetchFanCheck = async (userId: number, userIds: number[]) => {
  const { data } = await api.get<{
    fanClub: { name: string | null };
    users: { id: number; isFan: boolean }[];
  }>(
    `/users/${userId}/fan-club/users-check/${toQueryString({
      userIds: userIds.join(','),
    })}`
  );
  return data.users.map((user) => ({
    ...user,
    fanClubName: data.fanClub.name,
  }));
};

export const fetchFanClub = async (userId: number) => {
  const { data } = await api.get<IFanClub>(`/users/${userId}/fan-club/`);
  return data;
};

export const fetchFanClubUsers = async (
  userId: number,
  page = 1,
  perPage = 20
) => {
  const { data } = await api.get<IPagination<IFanUser>>(
    `/users/${userId}/fan-club/users/${toQueryString({ page, perPage })}`
  );

  return data;
};

export const fetchTopFanClubUsers = async (userId: number) => {
  const { data } = await api.get<IFanUser[]>(
    `/users/${userId}/fan-club/top-users/`
  );

  return data;
};

export const fetchMyFanClub = async () => {
  const { data } = await api.get<IFanClub>('/users/me/fan-club/');
  return data;
};

export type PatchMyFanClubPayload = {
  name: string;
  introduce: string;
  coverImage: FanClubCoverImage;
};

export type FanClubCoverImage = IEFinderObject<'image'>;

export const patchMyFanClub = async (payload: PatchMyFanClubPayload) => {
  const { data } = await api.patch<IUserMe>('/users/me/fan-club/', payload);
  return data;
};

/**
 * Voucher
 */

export const fetchVouchers = async (
  effectType: VoucherEffectType = VoucherEffectType.all,
  isAvailable?: boolean
) => {
  const { data } = await api.get<IVoucher[]>(
    `/users/me/vouchers/${toQueryString({ effectType, isAvailable })}`
  );
  return data;
};

export const fetchVoucherRecommendation = async (
  usage: VoucherRecommendationType,
  externalId?: number
) => {
  const { data } = await api.get<IVoucherRecommendation>(
    `/users/me/vouchers/recommendation/${toQueryString({ usage, externalId })}`
  );
  return data;
};

export const postVoucher = async (id: number) => {
  const { data } = await api.post(`/users/me/vouchers/${id}/consume/`);
  return data;
};

/**
 * Message
 */
export const fetchMessage = async (messageId: number) => {
  const { data } = await api.get<IMessage>(`/users/me/messages/${messageId}`);
  return data;
};

export const postMessage = async (
  userId: number,
  title: string,
  content: string,
  interpolation?: object
) => {
  const { data } = await api.post<IMessage>(`/users/${userId}/messages/`, {
    title,
    content,
    interpolation,
  });
  return data;
};

/**
 * Honor Partnership
 */
export type IHonorPartnershipValidationResult = {
  isPass: boolean;
};

export type IHonorPartnershipValidation = {
  hasPendingForm: boolean;
  isPartner: boolean;
  validationResult: {
    story: IHonorPartnershipValidationResult;
    communityGuideline: IHonorPartnershipValidationResult;
  };
};

export const getHonorPartnershipValidation = async () => {
  const { data } = await api.post<IHonorPartnershipValidation>(
    '/users/me/partnerships/honor/apply/validate/'
  );
  return data;
};

export type HonorPartnershipApplyPayload = {
  representStoryId: number;
  contactEmail: string;
  messageReview?: string;
  messageSuggestion?: string;
};

export const postHonorPartnership = async (
  payload: HonorPartnershipApplyPayload
) => {
  const { data } = await api.post(
    `/users/me/partnerships/honor/apply/`,
    payload
  );
  return data;
};

export type IRankedStory = Pick<IStory, 'id' | 'title' | 'coverImage' | 'user'>;
export type IHonorPartnershipDashboard = {
  partnership: {
    startedAt: string;
    representStory: {
      id: number;
      title: string;
    };
  };
  mission: {
    salesBalloon: number;
    salesBalloonExchanged: number;
    stampCount: number;
    stampDates: string[];
    publishingPeriodExpireAt: string;
    publishingPeriodViolationCount: number;
  };
  cumulativeInfo: {
    allBookingSalesBalloon: number;
    allBookingSalesBalloonExchanged: number;
    allDonationSalesBalloon: number;
    allDonationSalesBalloonExchanged: number;
    allWaitfreeSalesBalloon: number;
    allWaitfreeSalesBalloonExchanged: number;

    representStoryBookingSalesBalloon: number;
    representStoryBookingSalesBalloonExchanged: number;
    representStoryDonationSalesBalloon: number;
    representStoryDonationSalesBalloonExchanged: number;
    representStoryWaitfreeSalesBalloon: number;
    representStoryWaitfreeSalesBalloonExchanged: number;

    stampCount: number;
    totalPartnershipDays: number;
    totalActivitySupportAmount: number;
    totalActivitySupportPaymentCount: number;
    totalSupportedBalloon: number;
  };
  rankedStories: IRankedStory[];
};

export const fetchHonorPartnershipDashboard = async () => {
  const { data } = await api.get<IHonorPartnershipDashboard>(
    `/users/me/partnerships/honor/dashboard/`
  );
  return data;
};

export const postEmailCrendentialToUser = async (email: string) => {
  const { data } = await api.patch<IFirebaseEmailCredential>(
    '/users/me/email/',
    {
      email,
    }
  );

  return data;
};

export const postMyEmailVerification = async () => {
  const { data } = await api.post('/users/me/email/verification/');

  return data;
};

export const fetchSocialProviders = async () => {
  const { data } = await api.get('users/me/social/');

  return data;
};

export const postSocial = async (
  social: string,
  accessToken: string,
  idToken?: string
) => {
  const { data } = await api.post('/users/me/social-connect/', {
    social,
    accessToken,
    idToken,
  });
  return data;
};

export const disconnectSocial = async (social: string) => {
  const { data } = await api.post('/users/me/social-disconnect/', {
    social,
  });
  return data;
};

export const unregisterUser = async () => {
  const { data } = await api.delete('/users/me');

  return data;
};

export const patchMyEmail = async (email: string) => {
  const { data } = await api.patch('/users/me/email/', { email });

  return data;
};

export const checkSign = async (email: string) => {
  const { data } = await api.post('/sign/check/', {
    key: 'signup_by_email',
    value: {
      email,
    },
  });

  return data;
};

export const checkSocialSign = async (
  key: string,
  value: Record<string, string | undefined>
) => {
  const { data } = await api.post('/sign/check/', {
    key,
    value,
  });

  return data;
};

export const emailSignup = async ({
  email,
  password,
  nickname,
  birthday,
  gender,
  invitation_code,
}: {
  email: string;
  password: string;
  nickname: string;
  birthday?: string;
  gender?: string;
  invitation_code?: string;
}) => {
  const { data } = await api.post<ISignup>('/sign/providers/email/up/', {
    email,
    password,
    nickname,
    ...(birthday ? { birthday } : {}),
    ...(gender ? { gender } : {}),
    ...(invitation_code ? { invitation_code } : {}),
  });

  return data;
};

export const socialSignup = async ({
  social,
  accessToken,
  idToken,
  nickname,
  birthday,
  gender,
  invitation_code,
}: {
  social: string;
  accessToken: string;
  idToken?: string;
  nickname: string;
  birthday?: string;
  gender?: string;
  invitation_code?: string;
}) => {
  const { data } = await api.post<ISignup>('/sign/providers/social/up/', {
    social,
    accessToken,
    ...(idToken ? { idToken } : {}),
    nickname,
    ...(birthday ? { birthday } : {}),
    ...(gender ? { gender } : {}),
    ...(invitation_code ? { invitation_code } : {}),
  });

  return data;
};

export const socialSignin = async (
  social: string,
  accessToken: string,
  idToken?: string
) => {
  const { data } = await api.post('/sign/providers/social/in/', {
    social,
    accessToken,
    ...(idToken ? { idToken } : {}),
  });

  return data;
};

export const postMyCertification = async (
  transactionId: string,
  type = 'PL'
) => {
  const { data } = await api.post<ICertification>('/users/me/certification/', {
    transactionId,
    type,
  });

  return data;
};

export const guestSignin = async () => {
  const { data } = await api.post<IGuestToken>('/sign/guest/in/', {});
  return data;
};

export const fetchUserInvitationCode = async () => {
  const { data } = await api.get<IInvitationCode>('/users/me/invitation_code/');

  return data;
};

export const validateInvitationCode = async (code: string) => {
  const { data } = await api.get<{
    isValid: boolean;
    invalidReason: 'NOT_EXISTS_CODE' | 'INVITER_DELETED' | 'INVITER_SUSPENDED';
  }>(`/users/me/invitation_code/validity/?code=${code}`);

  return data;
};
export const fetchOneTimeToken = async () => {
  const { data } = await api.post<IOneTimeToken>('/sign/one-time-token/');

  return data;
};

export const fetchCustomToken = async (token: string) => {
  const { data } = await api.post<ICustomToken>('/sign/custom-token/', {
    one_time_token: token,
  });

  return data;
};

export const recoveryUserByInfo = async (info: string) => {
  const { data } = await api.post<IRecoveryUser>('/sign/recovery/', {
    value: info,
  });

  return data;
};

export const sendResetPasswordMail = async (value: string) => {
  const { data } = await api.post('/sign/password-reset/', {
    value,
  });

  return data;
};

export const recoveryUserByPhone = async (transactionId: string) => {
  const { data } = await api.post('/sign/recovery-by-certification/', {
    transactionId,
  });

  return data;
};
